import type { TrfSignatureChannel, TrfSignatureStatus } from '@/utils/DatabaseOperations'

/**
 * Interpret the TRF signature status on an order-details response (GEN-4030).
 *
 * provider-api projects the status of the signature request the order is currently
 * waiting on onto the order-details response; the SPA only reads it. The status arrives
 * in one vendor-neutral vocabulary whichever vendor produced it, and the mapping from a
 * status to "is this signed", "is anyone still expected to act", and what to show a
 * provider lives here rather than being re-derived at each call site.
 *
 * A status alone does not say enough to describe itself. The same word means different
 * things on the two signing channels — most sharply `sent`, which on the email channel
 * means the signer was mailed a link and on the embedded channel means nothing was
 * mailed anywhere and the document is waiting in-app — so the copy is chosen from the
 * status and the channel together.
 */

/** The status that means we have a signed TRF. */
const SIGNED_STATUS: TrfSignatureStatus = 'signed'

/**
 * Statuses after which no further transition is expected for that request. One of these
 * that is not `signed` is an order whose TRF will never be signed unless someone
 * intervenes.
 */
const TERMINAL_STATUSES: readonly TrfSignatureStatus[] = ['signed', 'declined', 'canceled', 'expired']

const KNOWN_STATUSES: readonly TrfSignatureStatus[] = [
  'created',
  'sent',
  'delivered',
  'signed',
  'declined',
  'canceled',
  'expired',
]

const KNOWN_CHANNELS: readonly TrfSignatureChannel[] = ['email', 'embedded']

/** How a signature status should read to a provider. */
export interface TrfSignatureDisplay {
  label: string
  /**
   * Semantic tone for the badge. `pending` is in flight, `success` is signed,
   * `warning` needs someone to act.
   */
  tone: 'pending' | 'success' | 'warning'
}

/** An order's TRF signature state, ready to render. */
export interface OrderTrfSignature {
  status: TrfSignatureStatus
  /** Vendor-namespaced id of the signature request the status came from. */
  externalDocumentId: string | null
  /** Which e-signature vendor produced the request, or null when not reported. */
  vendor: string | null
  /** When the vendor reported the transition, or null when the timestamp was unusable. */
  updatedAt: Date | null
  /**
   * How the request reaches its signer, or null when the backend did not report it.
   * Null is read as `email` everywhere it is consumed.
   */
  channel: TrfSignatureChannel | null
  /**
   * What the signer typed when they declined. Null when the status is not `declined`
   * or when they submitted the decline without a reason.
   */
  declineReason: string | null
  isSigned: boolean
  /** True when no further transition is expected for this request. */
  isTerminal: boolean
}

/**
 * Copy for a request the vendor mails to its signer.
 *
 * Also the copy for a request whose channel we could not determine: this is what every
 * order used before the embedded channel existed, so falling back to it can only
 * describe an older order the way it has always been described.
 */
const DISPLAY: Record<TrfSignatureStatus, TrfSignatureDisplay> = {
  created: { label: 'TRF Awaiting signature', tone: 'pending' },
  sent: { label: 'TRF Sent for signature', tone: 'pending' },
  delivered: { label: 'TRF Opened by signer', tone: 'pending' },
  signed: { label: 'TRF Signed', tone: 'success' },
  declined: { label: 'TRF Signature declined', tone: 'warning' },
  canceled: { label: 'TRF Signature request canceled', tone: 'warning' },
  expired: { label: 'TRF Signature request expired', tone: 'warning' },
}

/**
 * Where an embedded request needs different words, overriding {@link DISPLAY}.
 *
 * Only the two states whose emailed wording would be false for a captive recipient are
 * here, and both are false for the same reason — nothing was ever mailed:
 *
 * - `sent` says the request went out to the signer. Nothing went out; the document
 *   became signable in-app, and the clinician who is expected to sign it is the one
 *   reading this badge.
 * - `delivered` says the signer opened it. On the email channel that is the email being
 *   opened; on the embedded channel it is the document itself being opened in the
 *   ceremony, which is a signature part-way through rather than one not yet started.
 *
 * Every other state is deliberately absent. Signed, declined, canceled and expired
 * describe the request rather than its delivery, and read the same either way;
 * duplicating them would create two copies to keep in step for no gain.
 */
const EMBEDDED_DISPLAY: Partial<Record<TrfSignatureStatus, TrfSignatureDisplay>> = {
  sent: { label: 'TRF Ready for signature', tone: 'pending' },
  delivered: { label: 'TRF Signature in progress', tone: 'pending' },
}

/**
 * How to present a signature status to a provider.
 *
 * `channel` may be omitted or null, which reads as the emailed flow — see
 * {@link DISPLAY}.
 */
export function trfSignatureDisplay(
  status: TrfSignatureStatus,
  channel: TrfSignatureChannel | null = null
): TrfSignatureDisplay {
  if (channel === 'embedded') {
    return EMBEDDED_DISPLAY[status] ?? DISPLAY[status]
  }
  return DISPLAY[status]
}

function parseUpdatedAt(value: string | null | undefined): Date | null {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * The reported channel, or null for anything we do not recognize.
 *
 * A channel we cannot read falls back to the emailed copy, so an unrecognized value
 * costs an order its channel-specific wording rather than describing it wrongly.
 */
function parseChannel(value: string | null | undefined): TrfSignatureChannel | null {
  const raw = value?.trim()
  if (!raw) return null
  return KNOWN_CHANNELS.find((known) => known === raw) ?? null
}

/**
 * The signer's typed reason, or null when this is not a decline or they gave none.
 *
 * A reason on a non-declined status is ignored: the field is only meaningful on a
 * refused envelope, and a leftover value from a replaced request must not describe
 * the one currently on the order.
 */
function parseDeclineReason(status: TrfSignatureStatus, value: string | null | undefined): string | null {
  if (status !== 'declined') return null
  const raw = value?.trim()
  return raw || null
}

/**
 * TRF signature state from an order-details response, or null when no signature request
 * is outstanding for the order.
 *
 * Returns null for an unrecognized status as well: the backend records a status it does
 * not recognize rather than dropping it, so a vendor that invents one can still reach
 * the SPA. Showing nothing is better than showing a status we cannot describe.
 */
export function orderTrfSignatureFrom(
  order:
    | {
        trf_signature_status?: string | null
        trf_signature_external_document_id?: string | null
        trf_signature_vendor?: string | null
        trf_signature_updated_at?: string | null
        trf_signature_channel?: string | null
        trf_signature_decline_reason?: string | null
      }
    | null
    | undefined
): OrderTrfSignature | null {
  const raw = order?.trf_signature_status?.trim()
  if (!raw) return null

  const status = KNOWN_STATUSES.find((known) => known === raw)
  if (!status) return null

  return {
    status,
    externalDocumentId: order?.trf_signature_external_document_id?.trim() || null,
    vendor: order?.trf_signature_vendor?.trim() || null,
    updatedAt: parseUpdatedAt(order?.trf_signature_updated_at),
    channel: parseChannel(order?.trf_signature_channel),
    declineReason: parseDeclineReason(status, order?.trf_signature_decline_reason),
    isSigned: status === SIGNED_STATUS,
    isTerminal: TERMINAL_STATUSES.includes(status),
  }
}

/**
 * True when the order's outstanding signature request will never be signed without
 * intervention (declined, canceled, expired).
 *
 * A superseded request cannot reach this: provider-api reports only the request that
 * currently speaks for the order, so an order whose request was replaced is judged on
 * its replacement.
 *
 * This is the condition behind the backoffice unsigned-TRF alerts (GEN-4033).
 */
export function trfSignatureNeedsAttention(signature: OrderTrfSignature | null): boolean {
  if (!signature) return false
  return signature.isTerminal && !signature.isSigned
}
